import { z } from "zod";
import { ScoreBandSchema, type ScoreBand } from "./scoreBands.ts";

export const NineBoxCellIds = ["enigma", "growth", "stars", "dilemma", "core", "high-impact", "risk", "effective", "trusted"] as const;
export const NineBoxCellIdSchema = z.enum(NineBoxCellIds);
export type NineBoxCellId = z.infer<typeof NineBoxCellIdSchema>;

const CountSchema = z.number().int().nonnegative();

export const NineBoxCellSchema = z.object({
    id: NineBoxCellIdSchema,
    performanceBand: ScoreBandSchema,
    potentialBand: ScoreBandSchema,
    count: CountSchema,
    percentage: z.number().min(0).max(100)
});
export type NineBoxCell = z.infer<typeof NineBoxCellSchema>;

export const NineBoxResultSchema = z.object({
    cells: z.array(NineBoxCellSchema),
    totalCount: CountSchema
});
export type NineBoxResult = z.infer<typeof NineBoxResultSchema>;

export function assembleNineBox(counted: readonly Omit<NineBoxCell, "percentage">[]): NineBoxResult | undefined {
    if (counted.some(cell => !Number.isSafeInteger(cell.count) || cell.count < 0)) {
        return undefined;
    }

    const totalCount = counted.reduce((sum, cell) => sum + cell.count, 0);

    return {
        cells: counted.map(cell => ({ ...cell, percentage: totalCount > 0 ? Math.round((cell.count / totalCount) * 100) : 0 })),
        totalCount
    };
}

export interface NineBoxCellBands {
    performanceBand: ScoreBand;
    potentialBand: ScoreBand;
}

export const NINE_BOX_CELL_BANDS: Record<NineBoxCellId, NineBoxCellBands> = {
    enigma: { performanceBand: "Low", potentialBand: "High" },
    growth: { performanceBand: "Medium", potentialBand: "High" },
    stars: { performanceBand: "High", potentialBand: "High" },
    dilemma: { performanceBand: "Low", potentialBand: "Medium" },
    core: { performanceBand: "Medium", potentialBand: "Medium" },
    "high-impact": { performanceBand: "High", potentialBand: "Medium" },
    risk: { performanceBand: "Low", potentialBand: "Low" },
    effective: { performanceBand: "Medium", potentialBand: "Low" },
    trusted: { performanceBand: "High", potentialBand: "Low" }
};

export const NINE_BOX_BAND_POSITIONS: Record<ScoreBand, number> = { Low: 1, Medium: 2, High: 3 };

const CELL_ID_BY_BANDS: Record<ScoreBand, Record<ScoreBand, NineBoxCellId>> = {
    Low: { Low: "risk", Medium: "dilemma", High: "enigma" },
    Medium: { Low: "effective", Medium: "core", High: "growth" },
    High: { Low: "trusted", Medium: "high-impact", High: "stars" }
};

export function resolveNineBoxCellId(performanceBand: ScoreBand, potentialBand: ScoreBand): NineBoxCellId {
    return CELL_ID_BY_BANDS[performanceBand][potentialBand];
}
